<template>
  <input
    ref="customInput"
    :disabled="props.readonly || false"
    :readonly="props.readonly || false"
    class="custom-input"
    inputmode="numeric"
    v-bind="$attrs"
    @input="handleInput"
  />
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, toRefs, watch } from 'vue';
import AutoNumeric from 'autonumeric';

interface IProps {
  modelValue: number | undefined;
  maxDecimals: number;
  minimumValue: string;
  shouldBeUpdatedFlag: boolean;
  readonly?: boolean;
}

const props = defineProps<IProps>();

const emits = defineEmits(['update:modelValue', 'input']);

const { maxDecimals } = toRefs(props);

// inits
const customInput = ref();
const autoNumericInstance = ref<AutoNumeric>();

const commonOptions: AutoNumeric.Options = {
  allowDecimalPadding: false,
  alwaysAllowDecimalCharacter: true,
  decimalCharacterAlternative: ',',
  emptyInputBehavior: 'null',
  leadingZero: 'deny',
  watchExternalChanges: false, // for :value attribute
  minimumValue: props.minimumValue,
  selectOnFocus: false,
};

const defaultOptions = computed<AutoNumeric.Options>(() => ({
  ...commonOptions,
  decimalPlaces: maxDecimals.value,
  decimalPlacesRawValue: maxDecimals.value,
  decimalPlacesShownOnBlur: maxDecimals.value,
  decimalPlacesShownOnFocus: maxDecimals.value,
}));

onMounted(() => {
  autoNumericInstance.value = new AutoNumeric(
    customInput.value,
    '',
    defaultOptions.value,
  );

  // set the default value, if it exists
  if (props.modelValue == undefined || !props.shouldBeUpdatedFlag) return;
  autoNumericInstance.value?.set(props?.modelValue, formatter.value);
});

/**
 * Watches model changes and set the value in the input
 */
watch(
  () => props.modelValue,
  () => {
    if (
      !props.shouldBeUpdatedFlag &&
      autonumericInstanceValue.value === props.modelValue
    ) {
      return;
    }
    if (props.modelValue === undefined) {
      autoNumericInstance.value?.clear();
      return;
    }
    autoNumericInstance.value?.set(props?.modelValue || 0, formatter.value);
  },
);

const autonumericInstanceValue = computed(() =>
  autoNumericInstance.value?.getNumber(),
);

const formatter = computed(() => {
  const decimals = props.maxDecimals;
  return {
    ...commonOptions,
    decimalPlaces: decimals,
    decimalPlacesRawValue: decimals,
    decimalPlacesShownOnBlur: decimals,
    decimalPlacesShownOnFocus: decimals,
  };
});

watch(
  () => props.maxDecimals,
  () => {
    autoNumericInstance.value?.update(formatter.value);
  },
);

function handleInput(event: any) {
  event.preventDefault();
  /**
   * on android devices, the value comes from getNumber with a delay.
   * At the same time, it works on ios and desktop without delay.
   */
  setTimeout(() => {
    const rawNumber = autoNumericInstance.value?.getNumber() ?? undefined;
    emits('input', rawNumber);
  }, 10);
}
</script>

<style lang="scss" scoped>
.custom-input {
  border-radius: 1em;
  padding: 1em;
  font-size: 1em;
  width: 100%;
  background-color: #24243a;
  border: 1px solid rgba(229, 228, 250, 0);
  transition: all 0.3s ease-in-out;

  &:enabled {
    &:focus {
      box-shadow: none;
    }
  }

  &:hover {
    border: 1px solid rgba(229, 228, 250, 0.15);
  }

  &:focus {
    border: 1px solid rgba(229, 228, 250, 0.15);
  }

  &:active {
    border: 1px solid rgba(229, 228, 250, 0.15);
  }

  &:focus-visible {
    outline: none;
  }
}
</style>
